import type { MarkerBaseState, MarkerEditorProperties, IPoint } from '@markerjs/markerjs3'

/**
 * Types only - safe for SSR
 */
interface TimedPoint extends IPoint {
  timestamp: number
}

/**
 * Represents laser pointer marker's state.
 */
export interface LaserPointerMarkerState extends MarkerBaseState {
  /**
   * Points of the laser path.
   */
  points: Array<IPoint>
  /**
   * Whether the laser pointer is active.
   */
  isActive: boolean
  /**
   * Size of the laser dot
   */
  dotSize: number
}

if (typeof window === 'undefined' || typeof window.markerjs3 === 'undefined') {
  throw new Error('MarkerJS3 is not available.')
}

const { FreehandMarker, FreehandMarkerEditor, SvgHelper } = window.markerjs3

/**
 * A smooth laser pointer similar to Slack's huddle drawing tool
 * @summary Laser pointer marker
 * @group Markers
 */
export class LaserPointerMarker extends FreehandMarker {
  static typeName = 'LaserPointerMarker'
  static title = 'Laser pointer marker'
  static applyDefaultFilter = false

  /**
   * Points with timestamps for fade effect
   */
  protected timedPoints: TimedPoint[] = []

  /**
   * The main dot element - made public for editor access
   */
  public laserDot?: SVGCircleElement

  /**
   * The trail element
   */
  protected laserTrail?: SVGPathElement

  /**
   * Duration in milliseconds that trail points remain visible
   */
  protected trailDuration: number = 500

  /**
   * Animation frame ID
   */
  protected animationFrameId: number | null = null

  /**
   * Current pointer position
   */
  protected currentPosition: IPoint = { x: 0, y: 0 }

  /**
   * Whether the pointer is currently active/visible
   */
  protected isActive: boolean = false

  /**
   * Size of the laser dot
   */
  protected dotSize: number = 8

  constructor(container: SVGGElement) {
    super(container)
    this.strokeColor = '#ff3366' // Vibrant pink/red like Slack
    this.strokeWidth = 4
    this.opacity = 0.9

    // Start animation loop
    this.startAnimation()
  }

  /**
   * Get current dot size
   */
  get getDotSize(): number {
    return this.dotSize
  }

  /**
   * Set dot size
   */
  set setDotSize(value: number) {
    this.dotSize = value
    if (this.laserDot) {
      this.laserDot.setAttribute('r', (this.dotSize / 2).toString())
    }
  }

  /**
   * Updates the current position
   */
  updatePosition(point: IPoint): void {
    this.currentPosition = point

    // Add to timed points for trail
    const timedPoint: TimedPoint = {
      x: point.x,
      y: point.y,
      timestamp: Date.now(),
    }

    this.timedPoints.push(timedPoint)
    this.points.push(point) // Keep original points array updated

    // Keep only recent points for trail
    const now = Date.now()
    this.timedPoints = this.timedPoints.filter((p) => now - p.timestamp <= this.trailDuration)

    if (this.timedPoints.length > 50) {
      // Also limit by max points to avoid memory issues on fast movements
      this.timedPoints = this.timedPoints.slice(-50)
    }

    this.adjustVisual()
  }

  /**
   * Generates trail path based on recent points
   */
  getTrailPath(): string {
    if (this.timedPoints.length < 2) {
      return ''
    }

    const now = Date.now()

    // Filter to only include visible points within trail duration
    const visiblePoints = this.timedPoints
      .filter((p) => now - p.timestamp <= this.trailDuration)
      .sort((a, b) => a.timestamp - b.timestamp) // Sort by time (oldest first)

    if (visiblePoints.length < 2) {
      return ''
    }

    // Start path
    let pathData = `M ${visiblePoints[0].x} ${visiblePoints[0].y}`

    // Add lines to all subsequent points
    for (let i = 1; i < visiblePoints.length; i++) {
      pathData += ` L ${visiblePoints[i].x} ${visiblePoints[i].y}`
    }

    return pathData
  }

  /**
   * Starts animation loop
   */
  startAnimation(): void {
    const animate = () => {
      this.adjustVisual()
      this.animationFrameId = requestAnimationFrame(animate)
    }

    this.animationFrameId = requestAnimationFrame(animate)
  }

  /**
   * Stops animation loop
   */
  stopAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
  }

  /**
   * Creates the visual elements for the laser pointer
   */
  createVisual(): void {
    this.visual = SvgHelper.createGroup()

    // Create a gradient for the trail
    const defsElement = SvgHelper.createDefs()

    // Create linear gradient for the trail
    const gradientId = `laserTrailGradient-${Math.random().toString(36).substring(2, 11)}`
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient')
    gradient.setAttribute('id', gradientId)
    gradient.setAttribute('gradientUnits', 'userSpaceOnUse')

    // Gradient stops
    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
    stop1.setAttribute('offset', '0%')
    stop1.setAttribute('stop-color', this.strokeColor)
    stop1.setAttribute('stop-opacity', '0.1')

    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
    stop2.setAttribute('offset', '100%')
    stop2.setAttribute('stop-color', this.strokeColor)
    stop2.setAttribute('stop-opacity', '0.8')

    gradient.appendChild(stop1)
    gradient.appendChild(stop2)
    defsElement.appendChild(gradient)
    this.visual.appendChild(defsElement)

    // Create a trail path that follows the pointer
    this.laserTrail = SvgHelper.createPath('', [
      ['stroke', `url(#${gradientId})`],
      ['stroke-width', this.strokeWidth.toString()],
      ['fill', 'none'],
      ['stroke-linecap', 'round'],
      ['stroke-linejoin', 'round'],
    ])

    // Create the dot (the actual laser pointer)
    this.laserDot = SvgHelper.createCircle(this.dotSize, [
      ['fill', this.strokeColor],
      ['r', (this.dotSize / 2).toString()],
      ['filter', 'drop-shadow(0 0 3px rgba(255,255,255,0.5))'],
    ])

    // Add soft glow effect to dot
    const glowFilter = document.createElementNS('http://www.w3.org/2000/svg', 'filter')
    glowFilter.setAttribute('id', `glow-${Math.random().toString(36).substring(2, 11)}`)
    glowFilter.innerHTML = `
          <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        `
    defsElement.appendChild(glowFilter)
    this.laserDot.setAttribute('filter', `url(#${glowFilter.id})`)

    this.visual.appendChild(this.laserTrail)
    this.visual.appendChild(this.laserDot)

    this.addMarkerVisualToContainer(this.visual)

    // Hide initially
    this.setActive(false)
  }

  /**
   * Updates visual elements
   */
  adjustVisual(): void {
    if (!this.visual || !this.laserDot || !this.laserTrail) return

    // Update dot position
    this.laserDot.setAttribute('cx', this.currentPosition.x.toString())
    this.laserDot.setAttribute('cy', this.currentPosition.y.toString())

    // Update trail
    const trailPath = this.getTrailPath()
    this.laserTrail.setAttribute('d', trailPath)

    // Update gradient direction based on movement
    if (this.timedPoints.length >= 2) {
      const lastPoint = this.timedPoints[this.timedPoints.length - 1]
      const firstPoint = this.timedPoints[0]

      // Get the gradient element - proper type casting
      const gradientId = this.laserTrail.getAttribute('stroke')?.replace('url(#', '').replace(')', '') || ''
      const gradientElement = document.getElementById(gradientId)

      if (gradientElement && gradientElement instanceof SVGLinearGradientElement) {
        gradientElement.setAttribute('x1', firstPoint.x.toString())
        gradientElement.setAttribute('y1', firstPoint.y.toString())
        gradientElement.setAttribute('x2', lastPoint.x.toString())
        gradientElement.setAttribute('y2', lastPoint.y.toString())
      }
    }

    // Fade out inactive trail
    if (!this.isActive && this.timedPoints.length > 0) {
      const now = Date.now()
      const oldestPoint = this.timedPoints[0]
      const elapsedTime = now - oldestPoint.timestamp

      if (elapsedTime > this.trailDuration) {
        // Remove old points
        this.timedPoints.shift()
        if (this.timedPoints.length === 0) {
          this.laserTrail.setAttribute('d', '')
        }
      }
    }
  }

  /**
   * Sets active state of the laser pointer
   */
  setActive(active: boolean): void {
    this.isActive = active

    if (this.visual) {
      // Control visibility of the entire visual
      if (!active) {
        // Don't hide completely, just reduce opacity for fade effect
        this.visual.style.opacity = '0.3'
      } else {
        // Full opacity when actively drawing
        this.visual.style.opacity = '1'
      }
    }
  }

  /**
   * Completely removes the visual elements when the tool is no longer in use
   */
  clearVisuals(): void {
    if (this.visual) {
      // Clear the trail
      if (this.laserTrail) {
        this.laserTrail.setAttribute('d', '')
      }

      // Hide the entire visual
      this.visual.style.display = 'none'
    }

    // Clear points
    this.timedPoints = []
    this.points = []
  }

  /**
   * Disposes the marker and cleans up.
   */
  dispose(): void {
    super.dispose()
    this.stopAnimation()
    this.clearVisuals()
  }

  /**
   * Returns marker's state that can be restored in the future.
   */
  getState(): LaserPointerMarkerState {
    return Object.assign(
      {
        points: this.points,
        isActive: this.isActive,
        dotSize: this.dotSize,
      },
      super.getState(),
    )
  }

  /**
   * Restores previously saved marker state.
   *
   * @param state - previously saved state.
   */
  restoreState(state: MarkerBaseState): void {
    super.restoreState(state)
    const laserState = state as LaserPointerMarkerState
    this.points = laserState.points
    this.isActive = laserState.isActive || false
    this.dotSize = laserState.dotSize || 6

    // Recreate timed points from points
    const now = Date.now()
    this.timedPoints = this.points.map((p, index) => ({
      x: p.x,
      y: p.y,
      // Simulate timestamps with progressive offsets to create trail effect
      timestamp: now - Math.max(0, this.points.length - index - 1) * (this.trailDuration / this.points.length),
    }))

    this.createVisual()
    this.adjustVisual()
  }
}

/**
 * Editor for laser pointer markers.
 *
 * @summary Laser pointer marker editor.
 * @group Editors
 */
export class LaserPointerMarkerEditor extends FreehandMarkerEditor<LaserPointerMarker> {
  private lastPointerPosition: IPoint | null = null
  private isPointerDown: boolean = false

  constructor(properties: MarkerEditorProperties<LaserPointerMarker>) {
    super(properties)
    this._continuousCreation = true // Stay in laser pointer mode

    // Set custom cursor when tool is activated
    this.setupCustomCursor()
  }

  /**
   * Sets up the custom cursor for the laser pointer tool
   */
  private setupCustomCursor(): void {
    // Apply custom cursor to the container element
    if (this.container) {
      // Use a built-in cursor or custom SVG/URL
      this.container.style.cursor = 'none' // Hide the default cursor

      // You can also use a custom cursor image
      // this.container.style.cursor = 'url(laser-cursor.png) 4 4, auto'

      // Or use another built-in cursor type
      // this.container.style.cursor = 'crosshair'
    }
  }

  /**
   * Update laser pointer on mouse move
   */
  manipulate(point: IPoint): void {
    // Store the current position
    this.lastPointerPosition = point

    if (!this.marker.visual) {
      this.marker.createVisual()
    }

    // Always update position when mouse moves
    this.marker.updatePosition(point)

    // Show the laser when mouse button is down, hide otherwise
    if (this.isPointerDown) {
      this.marker.setActive(true)
    } else {
      // Still update position but keep invisible
      this.marker.setActive(false)
    }

    // Make sure cursor is always managed correctly
    this.updateCursorVisibility(point)
  }

  /**
   * Updates cursor visibility and position
   */
  private updateCursorVisibility(point: IPoint): void {
    // Ensure the custom dot follows the cursor perfectly
    if (this.marker.laserDot && this.container) {
      this.marker.laserDot.setAttribute('cx', point.x.toString())
      this.marker.laserDot.setAttribute('cy', point.y.toString())

      // Make sure dot is visible even when not drawing
      if (this.marker.visual) {
        this.marker.visual.style.display = 'block'
        this.marker.laserDot.style.opacity = this.isPointerDown ? '1' : '0.5'
      }
    }
  }

  /**
   * Show on mouse down
   */
  pointerDown(point: IPoint, target?: EventTarget, ev?: PointerEvent): void {
    super.pointerDown(point, target, ev)
    this.isPointerDown = true

    // Make sure visual exists
    if (!this.marker.visual) {
      this.marker.createVisual()
    }

    // Make sure the main container has the custom cursor
    this.setupCustomCursor()

    this.marker.updatePosition(point)
    this.marker.setActive(true)
  }

  /**
   * Hide on mouse up
   */
  pointerUp(point: IPoint, ev?: PointerEvent): void {
    super.pointerUp(point, ev)
    this.isPointerDown = false

    // Allow trail to fade out instead of immediately hiding
    // The fade-out will happen in the animation loop
    setTimeout(() => {
      if (!this.isPointerDown) {
        this.marker.setActive(false)
      }
    }, 100)
  }

  /**
   * Override select to ensure cursor is set correctly
   */
  select(): void {
    super.select()
    this.setupCustomCursor()
  }

  /**
   * Override deselect to restore default cursor
   */
  deselect(): void {
    super.deselect()

    // Restore default cursor when deselected
    if (this.container) {
      this.container.style.cursor = 'default'
    }

    // Completely hide the marker when deselected
    this.marker.clearVisuals()
  }

  /**
   * When user switches to a different tool or finishes using this tool
   */
  dispose(): void {
    super.dispose()

    // Ensure marker is completely cleared
    this.marker.clearVisuals()

    // Reset cursor
    if (this.container) {
      this.container.style.cursor = 'default'
    }
  }
}
